import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Inquiry() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: 'wedding',
    eventDate: '',
    guestCount: '50-100',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6alvstbd17lru29lthg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: 'wedding',
          eventDate: '',
          guestCount: '50-100',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section id="inquiry" className="py-32 bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-400/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="inline-flex items-center gap-3 px-6 py-2.5 bg-amber-500/10 backdrop-blur-sm rounded-full border border-amber-500/20 mb-6">
            <i className="ri-mail-send-line text-amber-400"></i>
            <span className="text-amber-300 text-sm font-medium tracking-widest uppercase">Get in Touch</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-serif font-bold text-white mb-5">
            Plan Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-200 to-amber-400">Event</span>
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto text-base leading-relaxed">
            Tell us about your event and we&apos;ll create a tailored proposal with equipment recommendations and pricing.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-20">
          {/* Left Content */}
          <div
            ref={leftRef}
            className={`scroll-animate-left ${leftVisible ? 'visible' : ''}`}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-amber-500 to-transparent"></div>
              <span className="text-amber-400 text-xs font-semibold tracking-[0.25em] uppercase">Why Choose Us</span>
            </div>

            <h3 className="text-4xl font-display font-normal text-white mb-6 leading-tight">
              Your Event,
              <br />
              <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-200">Perfectly Executed</span>
            </h3>

            <p className="text-white/50 mb-10 leading-relaxed text-lg">
              From the first consultation to the final cleanup, we handle every detail so you can enjoy your event stress-free.
            </p>

            {/* Features */}
            <div className={`space-y-6 mb-12 stagger-children ${leftVisible ? 'visible' : ''}`}>
              {[
                { icon: 'ri-truck-line', title: 'Free Delivery & Setup', desc: 'All equipment delivered and professionally arranged' },
                { icon: 'ri-shield-check-line', title: 'Fully Insured', desc: 'Complete coverage for your peace of mind' },
                { icon: 'ri-customer-service-2-line', title: 'Dedicated Coordinator', desc: 'A personal event manager from start to finish' },
              ].map((feature, i) => (
                <div key={i} className="flex items-start gap-5 group">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-400 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/20 group-hover:scale-110 transition-transform">
                    <i className={`${feature.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                    <p className="text-white/40 text-sm">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Card */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              <h4 className="font-serif text-xl text-white mb-6">Contact Information</h4>
              <div className="space-y-4">
                <a
                  href="mailto:hello@houseoffmr.com"
                  className="flex items-center gap-4 text-white/60 hover:text-amber-300 transition-colors cursor-pointer group"
                >
                  <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                    <i className="ri-mail-line text-amber-400"></i>
                  </div>
                  hello@houseoffmr.com
                </a>
                <a
                  href="tel:0411164144"
                  className="flex items-center gap-4 text-white/60 hover:text-amber-300 transition-colors cursor-pointer group"
                >
                  <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                    <i className="ri-phone-line text-amber-400"></i>
                  </div>
                  0411 164 144
                </a>
                <div className="flex items-center gap-4 text-white/60">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-amber-400"></i>
                  </div>
                  123 Event Lane, London, UK
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div
            ref={formRef}
            className={`relative scroll-animate-right ${formVisible ? 'visible' : ''}`}
          >
            <div className="absolute -inset-4 border border-amber-500/20 rounded-[2.5rem] hidden lg:block"></div>

            <div className="bg-white rounded-3xl p-10 shadow-2xl border border-stone-100 relative">
              <form
                id="event-inquiry-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50"
                      placeholder="+1 (234) 567-890"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Event Type *
                    </label>
                    <select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50 cursor-pointer"
                    >
                      <option value="wedding">Wedding</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="birthday">Birthday Party</option>
                      <option value="engagement">Engagement Party</option>
                      <option value="anniversary">Anniversary</option>
                      <option value="gala">Gala / Fundraiser</option>
                      <option value="equipment-only">Equipment Hire Only</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Event Date *
                    </label>
                    <input
                      type="date"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50 cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Guest Count
                    </label>
                    <select
                      name="guestCount"
                      value={formData.guestCount}
                      onChange={handleChange}
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50 cursor-pointer"
                    >
                      <option value="1-30">1 - 30 guests</option>
                      <option value="30-50">30 - 50 guests</option>
                      <option value="50-100">50 - 100 guests</option>
                      <option value="100-200">100 - 200 guests</option>
                      <option value="200+">200+ guests</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                    Tell Us About Your Event
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm resize-none bg-stone-50/50"
                    placeholder="Describe your vision, venue, equipment needs, or any special requirements..."
                  ></textarea>
                  <p className="text-xs text-stone-400 mt-2">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full py-5 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-white font-semibold rounded-xl hover:from-amber-400 hover:to-amber-300 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-amber-500/30 text-sm tracking-wide"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg"></i>
                      Send Inquiry
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-double-line text-amber-600 text-lg"></i>
                    </div>
                    <p className="text-amber-700 text-sm">
                      Thank you! We&apos;ll get back to you within 24 hours with a tailored proposal.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-5 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-rose-600 text-lg"></i>
                    </div>
                    <p className="text-rose-700 text-sm">
                      Something went wrong. Please try again or contact us directly.
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
